import React, { useState, useRef, useEffect } from "react";
import { Card, Button, Row, Col } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
import DocumentTemplateForm from "../DocumentTemplateForm/DocumentTemplateForm";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { handleAlert } from "@utils/helperFunctions";
import useGetDocumentTemplate from "./api/useGetDocumentTemplate";
import SmallSpinner from "@components/SmallSpinner/SmallSpinner";
import useOauth from "@utils/customHooks/useOauth";
import { PageTabs, type PageTab } from "@components/CatalogIndex";
import VersionHistory from "@components/VersionHistory";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import "./style.css";

type Tab = "template" | "versionHistory";

function Edit({ account, permission }) {
  const [authorizationCode] = useOauth();
  const [activeTab, setActiveTab] = useState<Tab>("template");
  const { hasPrivilege } = usePrivilegeCheck();
  const canViewAuditLogs = hasPrivilege("settings.audit_logs", [
    "view",
    "manage",
  ]);
  const canManageAuditLogs = hasPrivilege("settings.audit_logs", ["manage"]);

  const tabs: PageTab[] = [
    { key: "template", label: "Template" },
    ...(canViewAuditLogs
      ? [{ key: "versionHistory", label: "Version History" }]
      : []),
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const url = window.location.href;
  const id = url.split("/").slice(-1)?.[0] || "0";
  const { template, isLoading, isFetching, refetch } =
    useGetDocumentTemplate(id);
  const submitRef = useRef<any>();
  const [errorMessages, setErrorMessages] = useState<string | string[]>(
    "Something went wrong! Your changes could not be saved at this time."
  );
  const [showFailAlert, setShowFailAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState(
    "Document Template saved successfully!"
  );
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const [initializing, setInitializing] = useState(
    location?.["state"]?.new ? true : false
  );

  useEffect(() => {
    if (initializing) {
      setSuccessMessage("Document Template created!");
      setShowSuccessAlert(true);
      setTimeout(() => {
        refetch()
          .unwrap()
          .then(() => {
            navigate(location.pathname, { replace: true, state: {} });
            setInitializing(false);
          });
      }, 2000);
    }
  }, [initializing]);

  const handleDownload = async () => {
    const response = await fetch(template?.downloadhref, {
      headers: { Authorization: `Bearer ${authorizationCode}` },
    });
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download =
      template?.mergeTemplateFilename || template?.name || "download";
    a.click();

    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="editDocumentTemplatesPage">
      {handleAlert(
        showFailAlert,
        errorMessages,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <PageTabs
        activeKey={activeTab}
        onSelect={(key) => setActiveTab(key as Tab)}
        tabs={tabs}
      />
      <Card>
        <Card.Header className="headerOverride">
          <div className="headerContainer headerPadding">
            <div className="actionBtns">
              <Button
                className="btnSeawhite marginRight10"
                onClick={(e) =>
                  navigate(`/admin/document_templates`, {
                    state: {},
                  })
                }
              >
                Cancel
              </Button>
              <h2 className="headerText">{`Edit ${template?.name || ""}`}</h2>
            </div>

            <div className="actionBtns">
              {activeTab === "template" && permission !== "view" && (
                <>
                  {template?.downloadhref && (
                    <Button
                      className="btnSeawhite marginRight10"
                      style={{ width: "155px" }}
                      onClick={handleDownload}
                    >
                      Download Current
                    </Button>
                  )}
                  <Button
                    className="squareGreenButton"
                    onClick={() => {
                      submitRef.current?.saveDocumentTemplate();
                    }}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {isLoading || isFetching || initializing ? (
            <SmallSpinner />
          ) : activeTab === "versionHistory" ? (
            <VersionHistory
              filter={{
                "context-type": "DocumentTemplate",
                "context-id": id,
              }}
              permission={canManageAuditLogs}
              account={account?.data}
            />
          ) : (
            <DocumentTemplateForm
              documentTemplateProp={template}
              account={account}
              ref={submitRef}
              permission={permission}
              refetch={refetch}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
}

export default Edit;
